import React, { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Paper,
  Typography,
} from '@mui/material'
import axios from 'axios'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import ReplayIcon from '@mui/icons-material/Replay'
import { useQueryClient } from '@tanstack/react-query'
import useAutoCoder from '../../hooks/useAutoCoder'
import useAccount from '../../hooks/useAccount'
import useRouter from '../../hooks/useRouter'
import useLoading from '../../hooks/useLoading'
import { extractErrorMessage } from '../../utils/apitools'
import { API_BASE_URL } from '../../constants/system'
import { ImportResult, VariableNames } from '../../types/gotypes'
import FailedRecordsList from './FailedRecordsList'
import TSAPIPreviewModal from './TSAPIPreviewModal'
import ImportConfirmationModal from './ImportConfirmationModal'
import FolderBrowserModal from './FolderBrowserModal'

/**
 * The Output section (nav label "Output"; the id stays `status` so existing
 * `?section=status` links keep landing): the records that failed to code, and
 * the TSAPI export into Carbon.
 *
 * It used to also carry a progress bar and six per-state tiles. Those described
 * the same pipeline Throughput describes, in a second vocabulary over a
 * different span — two true percentages that disagreed on screen. Throughput now
 * owns both: a run-scoped bar, and a lifetime "Records by state" panel (T2).
 * What is left here is what has nowhere else to live.
 */
export interface PipelineImportStatusTabProps {
  customer: string
  job: string
  pipelineId: string
}

const PipelineImportStatusTab: React.FC<PipelineImportStatusTabProps> = ({
  customer,
  job,
  pipelineId,
}) => {
  const account = useAccount()
  const router = useRouter()
  const loading = useLoading()
  const queryClient = useQueryClient()
  const autoCoder = useAutoCoder({ customer, job, pipelineId })

  const [tsapiPreviewOpen, setTsapiPreviewOpen] = useState(false)
  const [tsapiPreviewTitle, setTsapiPreviewTitle] = useState('')
  const [tsapiPreviewData, setTsapiPreviewData] = useState<unknown>(null)
  const [tsapiPreviewUrl, setTsapiPreviewUrl] = useState('')
  const [tsapiLoading, setTsapiLoading] = useState(false)
  const [tsapiError, setTsapiError] = useState<string | null>(null)

  const [folderBrowserOpen, setFolderBrowserOpen] = useState(false)
  const [importConfirmOpen, setImportConfirmOpen] = useState(false)
  const [selectedFolderPath, setSelectedFolderPath] = useState('')
  const [importError, setImportError] = useState<string | null>(null)
  const [importResult, setImportResult] = useState<ImportResult | null>(null)

  const progress = autoCoder.progress
  const isProcessing = progress?.state === 'processing'
  // TSAPI export asks a DATA question — "is there anything coded to push into
  // Carbon?" — so it is gated on coded records, not on a lifecycle state.
  //
  // It used to test `state === 'completed' || state === 'imported'`. Those are
  // one-shot terminal states, and a long-lived pipeline never finishes, so
  // export was unreachable for the pipelines that accumulate the most. Worse,
  // the test did not even do what it looked like: a long-lived pipeline's State
  // CAN read `imported`, so the button already appeared for some and not others
  // on a field that means nothing to them.
  //
  // Exporting a snapshot from a standing pipeline is legitimate — it is
  // user-initiated and confirm-gated behind the folder browser and the import
  // dialog, so nothing is written to Carbon without an explicit choice.
  const hasCodedRecords = (progress?.completed ?? 0) > 0

  const importName = progress?.frameworks?.[0]?.main_variable?.name || 'import'
  const defaultVariablePath = `ThemeAI/${importName}`

  const handleViewTSAPI = async (type: 'metadata' | 'interviews') => {
    if (!pipelineId || !account.user) return
    loading.setLoading(true)
    setTsapiLoading(true)
    setTsapiError(null)
    const baseUrl = `${API_BASE_URL}/autocoder/${customer}/${job}/imports/${pipelineId}/tsapi/${type}`
    try {
      const response = await axios.get(baseUrl)
      setTsapiPreviewData(response.data)
      setTsapiPreviewTitle(type === 'metadata' ? 'TSAPI Metadata' : 'TSAPI Interview Data')
      setTsapiPreviewUrl(`${baseUrl}?token=${account.user.token}`)
      setTsapiPreviewOpen(true)
    } catch (error) {
      setTsapiError(`Failed to load ${type}: ${extractErrorMessage(error)}`)
    } finally {
      setTsapiLoading(false)
      loading.setLoading(false)
    }
  }

  const runImportTSAPI = async (variablePath: string, variableNames: VariableNames) => {
    if (!pipelineId || !account.user) return
    loading.setLoading(true)
    setTsapiLoading(true)
    setTsapiError(null)
    setImportError(null)
    try {
      const response = await axios.post(
        `${API_BASE_URL}/autocoder/${customer}/${job}/imports/${pipelineId}/tsapi/import`,
        { variable_path: variablePath, variable_names: variableNames },
      )
      setImportResult(response.data.import_result as ImportResult)
      setImportConfirmOpen(false)
      queryClient.invalidateQueries({ queryKey: ['vartree'] })
    } catch (error) {
      setImportError(extractErrorMessage(error))
    } finally {
      setTsapiLoading(false)
      loading.setLoading(false)
    }
  }

  const handleViewImportedVariables = () => {
    const firstFramework = autoCoder.frameworks?.[0]
    const baseName = importResult?.variables_created?.[0]?.split('_').slice(0, -1).join('_') || 'import'
    if (firstFramework?.main_variable?.name) {
      const highlightVar = `${baseName}_${firstFramework.main_variable.name}`
      setImportResult(null)
      router.navigate('variables', { customer, job, highlightVariable: highlightVar })
    }
  }

  if (!progress) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary">
          No coding progress for this pipeline yet.
        </Typography>
      </Box>
    )
  }

  return (
    <>
      <Box sx={{ p: 3 }}>
        {/* No progress bar and no per-state tiles here any more (T2/T3).
            Throughput owns progress: its backlog bar is scoped to the current
            run, and its "Records by state (lifetime)" panel carries the
            breakdown this section used to duplicate. Two bars for one pipeline,
            over different spans, disagreed by design.

            What is left is what has nowhere else to live: the records that
            failed, and the export into Carbon. */}
        {progress.failed === 0 && !hasCodedRecords && (
          <Typography variant="body2" color="text.secondary">
            Nothing needs attention here. Failed records and the Carbon export
            appear in this section when there are any.
          </Typography>
        )}

        {progress.failed > 0 && (
          <Paper elevation={1} sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Failed Records ({progress.failed})</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  color="info"
                  size="small"
                  startIcon={<FileDownloadIcon />}
                  onClick={() => {
                    if (!account.user) return
                    window.open(
                      `${API_BASE_URL}/autocoder/${customer}/${job}/pipelines/${pipelineId}/csv?state=failed&token=${account.user.token}`,
                      '_blank',
                    )
                  }}
                >
                  Export CSV
                </Button>
                {!isProcessing && (
                  <Button
                    variant="contained"
                    color="info"
                    size="small"
                    startIcon={<ReplayIcon />}
                    onClick={() => autoCoder.retryFailed(pipelineId)}
                    disabled={autoCoder.retryFailedLoading}
                  >
                    Retry Failed
                  </Button>
                )}
              </Box>
            </Box>
            <FailedRecordsList
              records={autoCoder.failedRecords}
              totalCount={autoCoder.failedRecordsCount}
              isLoading={autoCoder.failedRecordsLoading}
            />
          </Paper>
        )}

        {hasCodedRecords && (
          // Only offset when the failed-records panel is actually above it —
          // with the progress panels gone this is often the first thing here.
          <Paper elevation={1} sx={{ p: 3, mt: progress.failed > 0 ? 3 : 0 }}>
            <Typography variant="h6" gutterBottom>TSAPI Import</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              The TSAPI data structure that is used for importing results into Carbon.
            </Typography>
            {tsapiError && <Alert severity="error" sx={{ mb: 2 }}>{tsapiError}</Alert>}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button variant="outlined" onClick={() => handleViewTSAPI('metadata')} disabled={tsapiLoading}>
                Metadata
              </Button>
              <Button variant="outlined" onClick={() => handleViewTSAPI('interviews')} disabled={tsapiLoading}>
                Interview Data
              </Button>
              <Button
                variant="contained"
                color="info"
                onClick={() => {
                  setSelectedFolderPath(defaultVariablePath)
                  setFolderBrowserOpen(true)
                }}
                disabled={tsapiLoading}
              >
                Import Now
              </Button>
            </Box>
          </Paper>
        )}
      </Box>

      {tsapiPreviewOpen && tsapiPreviewData !== null && (
        <TSAPIPreviewModal
          open={tsapiPreviewOpen}
          title={tsapiPreviewTitle}
          jsonData={tsapiPreviewData}
          url={tsapiPreviewUrl}
          onClose={() => {
            setTsapiPreviewOpen(false)
            setTsapiPreviewData(null)
            setTsapiError(null)
          }}
        />
      )}

      <FolderBrowserModal
        open={folderBrowserOpen}
        customer={customer}
        job={job}
        currentPath={selectedFolderPath}
        onConfirm={(folderPath: string) => {
          setSelectedFolderPath(folderPath)
          setFolderBrowserOpen(false)
          setImportError(null)
          setImportConfirmOpen(true)
        }}
        onCancel={() => {
          setFolderBrowserOpen(false)
          setSelectedFolderPath('')
        }}
      />

      <ImportConfirmationModal
        open={importConfirmOpen || importResult !== null}
        importName={importName}
        variablePath={selectedFolderPath}
        frameworks={autoCoder.frameworks || []}
        error={importError}
        loading={tsapiLoading}
        importResult={importResult}
        onConfirm={runImportTSAPI}
        onCancel={() => {
          setImportConfirmOpen(false)
          setSelectedFolderPath('')
          setImportError(null)
        }}
        onChangeFolder={() => {
          setImportConfirmOpen(false)
          setFolderBrowserOpen(true)
        }}
        onViewVariables={handleViewImportedVariables}
      />
    </>
  )
}

export default PipelineImportStatusTab
